/* eslint-disable max-lines-per-function */
import {debounced} from '@mvps-genesis/utilities';
import {defineCustomElement} from 'extensions/ui-utils';
import {html, LitElement} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import {ifDefined} from 'lit/directives/if-defined.js';
import {live} from 'lit/directives/live.js';
import stylesheet from './x-quantity.scss?as=stylesheet';

/**
 * Used to determine if value was set via attribute
 * @type {boolean}
 */
let valueFromAttribute = false;

export class Quantity extends LitElement {
	static formAssociated = true;

	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			_value: {
				attribute: 'value',
				converter: value => {
					valueFromAttribute = true;
					return parseFloat(value);
				},
				reflect: true,
				type: Number
			},
			animate: {
				type: Boolean
			},
			disabled: {
				type: Boolean
			},
			hideButtons: {
				attribute: 'hide-buttons',
				type: Boolean
			},
			hideButtonsMobile: {
				attribute: 'hide-buttons-mobile',
				type: Boolean
			},
			hideButtonsTablet: {
				attribute: 'hide-buttons-tablet',
				type: Boolean
			},
			max: {
				type: Number
			},
			min: {
				type: Number
			},
			step: {
				type: Number
			}
		};
	}

	get form () {
		return this._internals.form;
	}

	get internalForm () {
		return this.renderRoot?.querySelector('form');
	}

	get input () {
		return this.renderRoot?.querySelector('input');
	}

	get value () {
		return this._value;
	}

	set value (newValue) {
		this._value = this._validateMinMaxOnChange(parseInt(newValue, 10) || this.min);
	}

	constructor () {
		super();

		/**
		 * @type {number}
		 */
		this._value = 1;

		/**
		 * @type {boolean}
		 */
		this.animate = false;

		/**
		 * @type {boolean}
		 */
		this.disabled = false;

		/**
		 * @type {boolean}
		 */
		this.hideButtons = false;

		/**
		 * @type {boolean}
		 */
		this.hideButtonsMobile = false;

		/**
		 * @type {boolean}
		 */
		this.hideButtonsTablet = false;

		/**
		 * @type {number}
		 */
		this.max = void 0;

		/**
		 * @type {number}
		 */
		this.min = 1;

		/**
		 * @type {number}
		 */
		this.step = 1;

		/**
		 * @type {ElementInternals}
		 */
		this._internals = this.attachInternals();

		// Create debounced handler
		this.debouncedChangeEvent = debounced(200, this._dispatchChangeEvent.bind(this));
	}

	incrementQuantity (n) {
		this.value += n;
		this.debouncedChangeEvent();
	}

	subtractQuantity () {
		this.incrementQuantity(this.step * -1);
	}

	addQuantity () {
		this.incrementQuantity(this.step);
	}

	_dispatchChangeEvent () {
		this.dispatchEvent(new CustomEvent('change', {
			bubbles: true,
			composed: false,
			detail: {
				value: this.value
			}
		}));
	}

	/**
	 * Prevent value from being out of bounds based off min/max properties
	 * @param {number} value
	 */
	_validateMinMaxOnChange (value) {
		// Handle max value
		if (this.max > 0 && value > this.max) {
			return Math.min(this.max, value);
		}

		if (this.min > 0 && value < this.min) {
			return Math.max(this.min, value);
		}

		return value;
	}

	async handleChange (e) {
		this.value = e.target.value;

		// Force rerender of actual input display
		await this.updateComplete;
		this.requestUpdate('_value', e.target.value);

		this.debouncedChangeEvent();
	}

	async updated (changedProperties) {
		if (changedProperties.has('_value')) {

			// Pass _value through setter if value was set via attribute assignment
			if (valueFromAttribute) {
				valueFromAttribute = false;

				await this.updateComplete;

				this.value = this._value;
			}

			this._internals.setFormValue(this.input?.value);
		}
	}

	render () {
		const classes = {
			animate: this.animate,
			container: true,
			'hide-buttons': this.hideButtons,
			'hide-buttons-mobile': this.hideButtonsMobile,
			'hide-buttons-tablet': this.hideButtonsTablet
		};

		return html`
			<div class="${classMap(classes)}">
				<button ?disabled="${parseInt(this.value, 10) <= this.min || this.disabled}" @click="${this.subtractQuantity}" type="button" class="button subtract-button" aria-label="Subtract Quantity"></button>
				<input max="${ifDefined(this.max)}" min="${this.min}" step="${this.step}" type="number" .value="${live(String(this.value))}" @change="${this.handleChange}" ?disabled="${this.disabled}" />
				<button ?disabled="${this.max && parseInt(this.value, 10) >= this.max || this.disabled}" @click="${this.addQuantity}" type="button" class="button add-button" aria-label="Add Quantity"></button>
			</div>
		`;
	}
};

defineCustomElement('x-quantity', Quantity);
